import type { FormConfig, FormField, FormVariant, LandingContext } from "./types";
import { markSubmitted } from "./triggers";
import { submitForm } from "./submit";
import { trackClick, trackPageClick } from "./beacon";
import { afterLandingSubmit } from "./landing";
import { youtubeId } from "./youtube";

const STYLE_ID = "hrn-forms-styles";

// The same form can render several times on one page (e.g. between every few
// races), so element ids carry a per-instance suffix.
let instanceCounter = 0;

// formId -> "show the success state" callbacks for every rendered instance, so a
// submit in one instance also settles its siblings. `withMedia` is true only for
// the instance the visitor actually used: a page with the same form between every
// few races should not load a video player in each of them.
type ShowSuccess = (message: string | undefined, withMedia: boolean) => void;
const successHandlers = new Map<string, ShowSuccess[]>();

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.hrnf-card{
  --hrnf-accent:#00529B; --hrnf-ink:#0b0b0b; --hrnf-muted:#52514e;
  --hrnf-surface:#fff; --hrnf-border:rgba(11,11,11,.12); --hrnf-radius:10px;
  --hrnf-button-ink:#fff; --hrnf-width:420px;
  font-family:Arial,Helvetica,"Open Sans",sans-serif; background:var(--hrnf-surface);
  border:1px solid var(--hrnf-border); border-radius:var(--hrnf-radius);
  width:100%; max-width:var(--hrnf-width); box-sizing:border-box; color:var(--hrnf-ink);
  display:flex; flex-direction:column; overflow:hidden; position:relative;
}
.hrnf-body{ padding:var(--hrnf-padding,20px); flex:1 1 0; min-width:0; display:flex; flex-direction:column; justify-content:center; }
.hrnf-media{ flex:0 0 auto; background:var(--hrnf-border); }
.hrnf-media img{ display:block; width:100%; height:100%; object-fit:cover; }
/* A top image keeps its own height up to 220px and is cropped (never squashed) past
   that; "contain" shows the whole image, so it may be taller. */
.hrnf-card[data-image="top"] .hrnf-media{ max-height:220px; overflow:hidden; }
.hrnf-card[data-image="top"] .hrnf-media img{ height:auto; max-height:220px; }
.hrnf-card[data-image="top"][data-fit="contain"] .hrnf-media{ max-height:360px; }
.hrnf-card[data-image="top"][data-fit="contain"] .hrnf-media img{ max-height:360px; }
/* Side by side only when there is room; in a narrow container (a phone, a sidebar)
   the image wraps above the text instead. */
.hrnf-card[data-image="left"],.hrnf-card[data-image="right"]{ flex-direction:row; flex-wrap:wrap; }
.hrnf-card[data-image="right"]{ flex-direction:row-reverse; }
.hrnf-card[data-image="left"] .hrnf-media,.hrnf-card[data-image="right"] .hrnf-media{ flex:1 1 200px; min-height:160px; position:relative; }
.hrnf-card[data-image="left"] .hrnf-media img,.hrnf-card[data-image="right"] .hrnf-media img{ position:absolute; inset:0; }
.hrnf-card[data-image="left"] .hrnf-body,.hrnf-card[data-image="right"] .hrnf-body{ flex:1.4 1 260px; }
/* Whole image, no crop: letterboxed on the card surface instead of the border tint. */
.hrnf-card[data-fit="contain"] .hrnf-media{ background:var(--hrnf-surface); }
.hrnf-card[data-fit="contain"] .hrnf-media img{ object-fit:contain; }
.hrnf-card[data-align="center"]{ margin:0 auto; }
.hrnf-card *{ box-sizing:border-box; }
.hrnf-headline{ font-size:20px; font-weight:700; margin:0 0 6px 0; line-height:1.25; }
.hrnf-subhead{ font-size:14px; color:var(--hrnf-muted); margin:0 0 16px 0; line-height:1.5; }
.hrnf-field{ margin-bottom:12px; }
.hrnf-label{ display:block; font-size:12px; font-weight:600; color:var(--hrnf-muted); margin-bottom:4px; }
.hrnf-input{
  width:100%; font-size:15px; padding:10px 12px; border:1px solid var(--hrnf-border);
  border-radius:6px; font-family:inherit;
}
.hrnf-input:focus{ outline:2px solid var(--hrnf-accent); outline-offset:1px; }
.hrnf-submit{
  width:100%; font-size:var(--hrnf-button-size,15px); font-weight:700; color:var(--hrnf-button-ink); background:var(--hrnf-accent);
  border:none; border-radius:var(--hrnf-button-radius,6px); padding:calc(var(--hrnf-button-size,15px) * .8) 16px; cursor:pointer;
}
.hrnf-submit:disabled{ opacity:.6; cursor:default; }
a.hrnf-submit{ display:block; text-align:center; text-decoration:none; }
a.hrnf-submit:hover{ text-decoration:none; color:var(--hrnf-button-ink); }
.hrnf-media a{ display:block; width:100%; height:100%; }
.hrnf-error{ font-size:13px; color:#b3261e; margin-top:8px; }
.hrnf-success{ font-size:15px; font-weight:600; color:var(--hrnf-ink); text-align:center; padding:8px 0; margin:0; }
.hrnf-success-media{ margin-top:12px; }
.hrnf-video{ position:relative; width:100%; padding-top:56.25%; background:#000; border-radius:6px; overflow:hidden; }
.hrnf-video iframe{ position:absolute; inset:0; width:100%; height:100%; border:0; }
.hrnf-watch{ display:block; text-align:center; font-size:13px; margin-top:8px; color:var(--hrnf-accent); }
.hrnf-success-image{ display:block; width:100%; height:auto; border-radius:6px; }
.hrnf-hp{ position:absolute; left:-9999px; width:1px; height:1px; overflow:hidden; }
.hrnf-overlay{
  position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex;
  align-items:center; justify-content:center; z-index:2147483000; padding:16px;
}
.hrnf-overlay .hrnf-card{ max-height:100%; overflow:auto; }
.hrnf-close{
  position:absolute; top:8px; right:8px; width:28px; height:28px; border:none; border-radius:50%;
  background:var(--hrnf-surface); font-size:16px; line-height:1; cursor:pointer; color:var(--hrnf-muted); z-index:1;
}
`;
  document.head.appendChild(style);
}

function applyTheme(el: HTMLElement, theme?: Record<string, string>): void {
  if (!theme) return;
  for (const [key, value] of Object.entries(theme)) {
    el.style.setProperty(`--hrnf-${key}`, value);
  }
}

function buildFieldEl(field: FormField, instance: number): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "hrnf-field";
  if (field.type === "hidden") {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = field.name;
    input.value = field.value ?? "";
    return input;
  }
  if (field.label) {
    const label = document.createElement("label");
    label.className = "hrnf-label";
    label.textContent = field.label;
    label.htmlFor = `hrnf-${field.name}-${instance}`;
    wrap.appendChild(label);
  }
  const input = document.createElement("input");
  input.className = "hrnf-input";
  input.id = `hrnf-${field.name}-${instance}`;
  input.name = field.name;
  input.type = field.type === "checkbox" ? "checkbox" : field.type;
  if (field.placeholder) input.placeholder = field.placeholder;
  if (field.required) input.required = true;
  wrap.appendChild(input);
  return wrap;
}

/** The post-sign-up reveal: a YouTube video and/or an image. Null when the form has neither. */
function buildSuccessMedia(config: FormConfig): HTMLElement | null {
  const videoId = youtubeId(config.successVideoUrl);
  const imageUrl =
    config.successImageUrl && /^https:\/\//.test(config.successImageUrl) ? config.successImageUrl : null;
  if (!videoId && !imageUrl) return null;

  const wrap = document.createElement("div");

  if (videoId) {
    const block = document.createElement("div");
    block.className = "hrnf-success-media";
    const frame = document.createElement("div");
    frame.className = "hrnf-video";
    const iframe = document.createElement("iframe");
    // Built from the validated id only — never from the configured URL.
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;
    iframe.title = "Video";
    iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
    iframe.allowFullscreen = true;
    iframe.loading = "lazy";
    // YouTube refuses embeds that send no referrer.
    iframe.referrerPolicy = "strict-origin-when-cross-origin";
    frame.appendChild(iframe);
    block.appendChild(frame);

    // For browsers and extensions that block the player.
    const watch = document.createElement("a");
    watch.className = "hrnf-watch";
    watch.href = `https://www.youtube.com/watch?v=${videoId}`;
    watch.target = "_blank";
    watch.rel = "noopener";
    watch.textContent = "Watch on YouTube";
    block.appendChild(watch);
    wrap.appendChild(block);
  }

  if (imageUrl) {
    const block = document.createElement("div");
    block.className = "hrnf-success-media";
    const img = document.createElement("img");
    img.className = "hrnf-success-image";
    img.src = imageUrl;
    img.alt = config.successImageAlt ?? "";
    block.appendChild(img);
    wrap.appendChild(block);
  }

  return wrap;
}

interface BuiltForm {
  card: HTMLElement;
  /** Puts this instance into its success state without a submit (previews). A link block has none. */
  showSuccess: () => void;
}

const HTTPS = /^https:\/\//;

function isLink(config: FormConfig): boolean {
  return config.blockType === "link";
}

/**
 * Counts a click on a link block on the way out: on the block, and on the landing
 * page it is the conversion of (a preview counts nothing).
 */
function onLinkClick(apiBase: string, config: FormConfig, variant: FormVariant, landing?: LandingContext): void {
  trackClick(apiBase, config.id, variant.id);
  if (landing && !landing.preview) {
    trackPageClick(apiBase, landing.pageId, landing.pageVariantId ?? "", landing.track ?? "form");
  }
}

/** The button of a link block: an anchor to the variant's ctaUrl that counts a click on the way out. */
function buildLinkButton(
  apiBase: string,
  config: FormConfig,
  variant: FormVariant,
  href: string,
  landing?: LandingContext
): HTMLElement {
  const link = document.createElement("a");
  link.className = "hrnf-submit";
  link.href = href;
  link.textContent = variant.ctaLabel;
  link.addEventListener("click", () => onLinkClick(apiBase, config, variant, landing));
  return link;
}

function buildForm(
  apiBase: string,
  config: FormConfig,
  variant: FormVariant,
  /** `hasMedia`: the success state is showing a video or image the visitor may want to stay on. */
  onDone: (hasMedia: boolean) => void,
  landing?: LandingContext
): BuiltForm {
  ensureStyles();
  const instance = ++instanceCounter;

  const card = document.createElement("div");
  card.className = "hrnf-card";
  applyTheme(card, config.theme);
  if (config.width) card.style.setProperty("--hrnf-width", `${config.width}px`);
  if (config.height) card.style.minHeight = `${config.height}px`;
  if (config.align === "center") card.dataset.align = "center";

  // A link block's destination. Anything but https is treated as "no link": the URL
  // is published config, and nothing else belongs in an href on HRN's pages.
  const linkHref = isLink(config) && variant.ctaUrl && HTTPS.test(variant.ctaUrl) ? variant.ctaUrl : null;

  let variantMedia: HTMLElement | null = null;
  if (variant.imageUrl && HTTPS.test(variant.imageUrl)) {
    card.dataset.image = config.imagePosition ?? "top";
    if (config.imageFit === "contain") card.dataset.fit = "contain";
    const media = document.createElement("div");
    variantMedia = media;
    media.className = "hrnf-media";
    const img = document.createElement("img");
    img.src = variant.imageUrl;
    img.alt = variant.imageAlt ?? "";
    img.loading = "lazy";
    if (linkHref) {
      // On a link block the photo goes to the same place as the button.
      const imageLink = document.createElement("a");
      imageLink.href = linkHref;
      imageLink.setAttribute("aria-label", variant.ctaLabel);
      imageLink.addEventListener("click", () => onLinkClick(apiBase, config, variant, landing));
      imageLink.appendChild(img);
      media.appendChild(imageLink);
    } else {
      media.appendChild(img);
    }
    card.appendChild(media);
  }

  const body = document.createElement("div");
  body.className = "hrnf-body";
  card.appendChild(body);

  if (variant.headline) {
    const headline = document.createElement("p");
    headline.className = "hrnf-headline";
    headline.textContent = variant.headline;
    body.appendChild(headline);
  }

  if (variant.subhead) {
    const subhead = document.createElement("p");
    subhead.className = "hrnf-subhead";
    subhead.textContent = variant.subhead;
    body.appendChild(subhead);
  }

  if (isLink(config)) {
    if (linkHref) body.appendChild(buildLinkButton(apiBase, config, variant, linkHref, landing));
    else console.error(`hrn-forms: link block "${config.id}" variant "${variant.id}" has no https ctaUrl`);
    return { card, showSuccess: () => {} };
  }

  const form = document.createElement("form");
  form.noValidate = true;

  const fields = variant.fields ?? config.fields ?? [];
  for (const field of fields) form.appendChild(buildFieldEl(field, instance));

  // Honeypot — visually hidden, real users never fill it.
  const hp = document.createElement("input");
  hp.type = "text";
  hp.name = "hp_field";
  hp.className = "hrnf-hp";
  hp.tabIndex = -1;
  hp.autocomplete = "off";
  form.appendChild(hp);

  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className = "hrnf-submit";
  submitBtn.textContent = variant.ctaLabel;
  form.appendChild(submitBtn);

  const errorEl = document.createElement("p");
  errorEl.className = "hrnf-error";
  errorEl.hidden = true;
  form.appendChild(errorEl);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    submitBtn.disabled = true;
    errorEl.hidden = true;

    submitForm(apiBase, config, variant, form, landing).then((result) => {
      if (result.ok) {
        markSubmitted(config.id);
        // On a landing page the page may take over from here (thank-you page or
        // redirect); the inline success state only shows when it does not.
        if (landing && afterLandingSubmit(landing)) return;
        for (const show of successHandlers.get(config.id) ?? []) show(result.message, show === showSuccess);
      } else {
        submitBtn.disabled = false;
        errorEl.textContent = result.message ?? "Something went wrong. Please try again.";
        errorEl.hidden = false;
      }
    });
  });

  const showSuccess: ShowSuccess = (message, withMedia) => {
    // Only the body is replaced: the photo and the card's size stay put, so the
    // page around an inline form does not jump.
    body.innerHTML = "";
    const success = document.createElement("p");
    success.className = "hrnf-success";
    success.textContent = message ?? "Thanks — you're all set!";
    body.appendChild(success);

    const media = withMedia ? buildSuccessMedia(config) : null;
    if (media) {
      // The reveal gets the whole card: beside a side-by-side photo it would be
      // squeezed into the narrow text column.
      variantMedia?.remove();
      delete card.dataset.image;
      body.appendChild(media);
    }
    onDone(media !== null);
  };

  const handlers = successHandlers.get(config.id) ?? [];
  handlers.push(showSuccess);
  successHandlers.set(config.id, handlers);

  body.appendChild(form);
  return { card, showSuccess: () => showSuccess(config.successMessage, true) };
}

export interface RenderOptions {
  /** Render straight into the success state — the preview page's ?state=success. */
  previewSuccess?: boolean;
  /** Set when mounted on one of the form's landing pages. */
  landing?: LandingContext;
}

export function renderInline(
  apiBase: string,
  config: FormConfig,
  variant: FormVariant,
  target: Element,
  options: RenderOptions = {}
): void {
  const built = buildForm(apiBase, config, variant, () => {}, options.landing);
  target.appendChild(built.card);
  if (options.previewSuccess) built.showSuccess();
}

export function renderPopup(
  apiBase: string,
  config: FormConfig,
  variant: FormVariant,
  options: RenderOptions = {}
): void {
  const overlay = document.createElement("div");
  overlay.className = "hrnf-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");

  const close = () => overlay.remove();

  // A plain thank-you closes itself. One with a video or image stays until the
  // visitor closes it — that reveal is the point of the popup.
  const built = buildForm(apiBase, config, variant, (hasMedia) => {
    if (!hasMedia) window.setTimeout(close, 1800);
  });
  const card = built.card;

  const closeBtn = document.createElement("button");
  closeBtn.className = "hrnf-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  closeBtn.addEventListener("click", close);
  card.prepend(closeBtn);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener("keydown", function onKey(e) {
    if (e.key === "Escape") {
      close();
      document.removeEventListener("keydown", onKey);
    }
  });

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const firstInput = card.querySelector<HTMLInputElement>(".hrnf-input");
  firstInput?.focus();
  if (options.previewSuccess) built.showSuccess();
}
